import { getConfig, getContext, getElement, store } from '@wordpress/interactivity';

type ServerState = {
	state: {
		data?: Record< string, { currentPage: number; query: object } >;
	};
};

type Context = {
	instanceId: string;
};

type Config = {
	restURL: string;
	restNonce?: string;
};

const storeDef = {
	state: {
		currentInstanceId: '',
		isLoadingMap: {},
		loadedPagesMap: {},
		hasMorePagesMap: {},
		get isButtonHidden() {
			if ( ! state.currentInstanceId ) {
				return false;
			}

			return state.hasMorePagesMap[ state.currentInstanceId ] === false;
		},
		get currentQuery() {
			if ( ! state.currentInstanceId || ! state.data ) {
				return null;
			}

			return state.data[ state.currentInstanceId ].query || null;
		},
		get isLoading() {
			if ( ! state.currentInstanceId ) {
				return false;
			}

			return state.isLoadingMap[ state.currentInstanceId ];
		},
	},
	actions: {
		*loadMore() {
			const { instanceId } = getContext< Context >();

			// Bails out if the query is not set.
			if ( ! state?.data?.[ instanceId ].query ) {
				return;
			}

			const { ref } = getElement();

			const queryBlockWrapper = ref?.closest( '.wp-block-query' );
			if ( ! queryBlockWrapper ) {
				return;
			}

			state.currentInstanceId = instanceId;

			const { restURL, restNonce }: Config = getConfig();

			const headers = {
				'Content-Type': 'application/json',
			};

			if ( restNonce ) {
				headers[ 'X-WP-Nonce' ] = restNonce;
			}

			try {
				state.isLoadingMap = {
					...state.isLoadingMap,
					[ state.currentInstanceId ]: true,
				};

				const nextPage = ( state.loadedPagesMap[ state.currentInstanceId ] || 1 ) + 1;

				const response = yield window.fetch( restURL, {
					method: 'POST',
					headers,
					credentials: 'include',
					body: JSON.stringify( {
						query: state.currentQuery,
						page: nextPage,
					} ),
				} );

				if ( response.status !== 200 ) {
					throw new Error( 'Invalid response' );
				}

				const data = yield response.json();

				yield ( state.loadedPagesMap = {
					...state.loadedPagesMap,
					[ state.currentInstanceId ]: nextPage,
				} );

				yield ( state.hasMorePagesMap = {
					...state.hasMorePagesMap,
					[ state.currentInstanceId ]: data.has_more,
				} );

				const newDom = yield new window.DOMParser().parseFromString(
					data.html,
					'text/html'
				);
				const newPostTemplate =
					newDom.querySelector( '.wp-block-post-template' )?.innerHTML;

				if ( ! newPostTemplate ) {
					throw new Error( "Couldn't find the post template in the response." );
				}

				yield queryBlockWrapper
					.querySelector( '.wp-block-post-template' )
					?.insertAdjacentHTML( 'beforeend', newPostTemplate );
			} catch ( error ) {
				console.error( error ); // eslint-disable-line no-console
			} finally {
				yield ( state.isLoadingMap = {
					...state.isLoadingMap,
					[ state.currentInstanceId ]: false,
				} );
			}
		},
	},
};

type Store = ServerState & typeof storeDef;

const { state } = store< Store >( 'swisscom/infinite-scroll', storeDef, { lock: true } );
